import { ReactNode, FC, createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  OrgData,
  orgData as staticOrgData,
  Option,
  Platform,
  Chapter,
  Division,
  platformOptions as staticPlatformOptions,
  externalRadarSources,
  ExternalRadarSource,
} from '@internal/plugin-org-data-common';
import { orgDataApiRef, OrgDivision, OrgPlatform, OrgChapter } from '../api';

/** Reconstruct nested Division[] from the flat API entities. */
function buildDivisions(
  apiDivisions: OrgDivision[],
  apiPlatforms: OrgPlatform[],
  apiChapters: OrgChapter[],
): Division[] {
  return apiDivisions.map(div => ({
    id: div.id,
    name: div.name,
    platforms: apiPlatforms
      .filter(p => p.division_id === div.id)
      .map(platform => ({
        id: platform.id,
        name: platform.name,
        chapters: apiChapters
          .filter(ch => ch.platform_id === platform.id)
          .map(ch => ({ id: ch.id, name: ch.name })),
      })),
  }));
}

export interface OrgContextValue {
  // Dynamic org data. While loading, falls back to static seed data.
  // On error, returns empty arrays so consumers can surface a meaningful error
  // rather than silently showing stale or permission-bypassing static data.
  orgData: OrgData;
  divisions: Division[];
  allPlatforms: Platform[];
  allChapters: Chapter[];
  externalRadarSources: ExternalRadarSource[];
  /** True while the initial API fetch is in flight. */
  loading: boolean;
  /** Set when the API fetch fails; null otherwise. */
  error: Error | null;

  // Options for dropdowns
  divisionOptions: Option[];
  platformOptions: Record<string, Option[]>;

  // Utility functions
  getPlatformsByDivision: (divisionId: string) => Platform[];
  getChaptersByPlatform: (platformId: string) => Chapter[];
  getPlatformById: (platformId: string) => Platform | undefined;
  filterPlatformsForDivisions: (divisionIds: string[]) => Option[];
  validatePlatformsForDivisions: (platformIds: string[], divisionIds: string[]) => string[];
  /** Re-fetch all org data from the backend (call after any create/update/delete mutation). */
  refetch: () => Promise<void>;
}

const OrgContext = createContext<OrgContextValue | undefined>(undefined);

export const useOrgContext = (): OrgContextValue => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrgContext must be used within an OrgProvider');
  }
  return context;
};

interface OrgProviderProps {
  children: ReactNode;
}

export const OrgProvider: FC<OrgProviderProps> = ({ children }) => {
  const orgDataApi = useApi(orgDataApiRef);

  const [apiDivisions, setApiDivisions] = useState<OrgDivision[]>([]);
  const [apiPlatforms, setApiPlatforms] = useState<OrgPlatform[]>([]);
  const [apiChapters, setApiChapters] = useState<OrgChapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [divisionsResult, platformsResult, chaptersResult] = await Promise.all([
        orgDataApi.getDivisions(),
        orgDataApi.getPlatforms(),
        orgDataApi.getChapters(),
      ]);
      setApiDivisions(divisionsResult.divisions);
      setApiPlatforms(platformsResult.platforms);
      setApiChapters(chaptersResult.chapters);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [orgDataApi]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Warn when the backend returns divisions but no platforms (partial data).
  useEffect(() => {
    if (!loading && !error && apiDivisions.length > 0 && apiPlatforms.length === 0) {
      // eslint-disable-next-line no-console
      console.warn(
        'OrgProvider: backend returned divisions but zero platforms. ' +
          'This appears to be partial org-data; divisions will render without platforms until platforms are available.',
      );
    }
  }, [loading, error, apiDivisions, apiPlatforms]);

  // ── Derived, memoised data ───────────────────────────────────────────────

  const { divisions, allPlatforms, allChapters, divisionOptions, platformOptions } =
    useMemo(() => {
      // Use API data when the backend has returned at least one division.
      // Only fall back to static seed data when the backend returned nothing at
      // all (not yet seeded). Never silently fall back on error – return empty
      // arrays instead so consumers can surface a meaningful error message and
      // avoid accidentally bypassing RBAC-driven visibility.
      const backendHasData = !loading && !error && apiDivisions.length > 0;
      const useStaticFallback = !backendHasData && !error;

      let divs: Division[];
      if (backendHasData) {
        divs = buildDivisions(apiDivisions, apiPlatforms, apiChapters);
      } else if (useStaticFallback) {
        divs = staticOrgData.divisions;
      } else {
        divs = [];
      }

      const platforms: Platform[] = divs.flatMap(d => d.platforms);
      const chapters: Chapter[] = platforms.flatMap(p => p.chapters ?? []);
      const divOptions: Option[] = divs.map(d => ({ label: d.name, value: d.id }));

      // Only compute platform options map when backend data is available;
      // otherwise keep the static keyed map so existing consumers still work.
      let platOptions: Record<string, Option[]>;
      if (backendHasData) {
        platOptions = divs.reduce(
          (acc, div) => {
            acc[div.id] = div.platforms.map(p => ({ label: p.name, value: p.id }));
            return acc;
          },
          {} as Record<string, Option[]>,
        );
      } else if (useStaticFallback) {
        platOptions = staticPlatformOptions;
      } else {
        platOptions = {};
      }

      return {
        divisions: divs,
        allPlatforms: platforms,
        allChapters: chapters,
        divisionOptions: divOptions,
        platformOptions: platOptions,
      };
    }, [loading, error, apiDivisions, apiPlatforms, apiChapters]);

  // ── Memoised utility functions ───────────────────────────────────────────

  const getPlatformsByDivision = useCallback(
    (divisionId: string): Platform[] =>
      divisions.find(d => d.id === divisionId)?.platforms ?? [],
    [divisions],
  );

  const getChaptersByPlatform = useCallback(
    (platformId: string): Chapter[] =>
      allPlatforms.find(p => p.id === platformId)?.chapters ?? [],
    [allPlatforms],
  );

  const getPlatformById = useCallback(
    (platformId: string): Platform | undefined =>
      allPlatforms.find(p => p.id === platformId),
    [allPlatforms],
  );

  const filterPlatformsForDivisions = useCallback(
    (divisionIds: string[]): Option[] =>
      divisionIds.flatMap(divisionId => platformOptions[divisionId] ?? []),
    [platformOptions],
  );

  const validatePlatformsForDivisions = useCallback(
    (platformIds: string[], divisionIds: string[]): string[] => {
      const validPlatformIds = filterPlatformsForDivisions(divisionIds).map(
        option => option.value,
      );
      return platformIds.filter(platformId => validPlatformIds.includes(platformId));
    },
    [filterPlatformsForDivisions],
  );

  // ── Stable context value ─────────────────────────────────────────────────

  const value = useMemo(
    (): OrgContextValue => ({
      orgData: { divisions },
      divisions,
      allPlatforms,
      allChapters,
      externalRadarSources,
      loading,
      error,
      divisionOptions,
      platformOptions,
      getPlatformsByDivision,
      getChaptersByPlatform,
      getPlatformById,
      filterPlatformsForDivisions,
      validatePlatformsForDivisions,
      refetch: loadData,
    }),
    [
      divisions,
      allPlatforms,
      allChapters,
      loading,
      error,
      divisionOptions,
      platformOptions,
      getPlatformsByDivision,
      getChaptersByPlatform,
      getPlatformById,
      filterPlatformsForDivisions,
      validatePlatformsForDivisions,
      loadData,
    ],
  );

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
};